import { computed } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useApi, useToaster } from '@shopexperts/shared';

export function useRespondToReview() {
  const api = useApi();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  const respondMutation = useMutation({
    mutationKey: ['expert', 'respondToReview'],
    mutationFn: async ({ reviewId, responseText }: { reviewId: number; responseText: string }) => {
      return await api.expert.reviewsV2.respondToReview({
        params: { review: reviewId },
        body: { response_text: responseText },
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['expert', 'reviews'] });
      queryClient.invalidateQueries({ queryKey: ['v2projects', 'review'] });
      
      toaster.create({
        title: 'Response added successfully!',
        description: 'Your response to the review has been published.',
        type: 'success',
      });
    },
    onError: (error: any) => {
      console.error('[useRespondToReview] Error:', error);
      
      const errorMessage = 
        error?.response?.data?.message || 
        error?.response?.data?.error ||
        error?.message ||
        'Failed to add response. Please try again.';
      
      toaster.create({
        title: 'Error adding response',
        description: errorMessage,
        type: 'error',
      });
    },
  });

  const updateMutation = useMutation({
    mutationKey: ['expert', 'updateResponse'],
    mutationFn: async ({ reviewId, responseText }: { reviewId: number; responseText: string }) => {
      return await api.expert.reviewsV2.updateResponse({
        params: { review: reviewId },
        body: { response_text: responseText },
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['expert', 'reviews'] });
      queryClient.invalidateQueries({ queryKey: ['v2projects', 'review'] });
      
      toaster.create({
        title: 'Response updated successfully!',
        description: 'Your response to the review has been updated.',
        type: 'success',
      });
    },
    onError: (error: any) => {
      console.error('[useRespondToReview] Error:', error);
      
      const errorMessage = 
        error?.response?.data?.message || 
        error?.response?.data?.error ||
        error?.message ||
        'Failed to update response. Please try again.';
      
      toaster.create({
        title: 'Error updating response',
        description: errorMessage,
        type: 'error',
      });
    },
  });

  const isSubmitting = computed(() => respondMutation.isPending.value || updateMutation.isPending.value);

  const respondToReview = (reviewId: number, responseText: string) => {
    return respondMutation.mutateAsync({ reviewId, responseText });
  };

  const updateResponse = (reviewId: number, responseText: string) => {
    return updateMutation.mutateAsync({ reviewId, responseText });
  };

  return {
    respondToReview,
    updateResponse,
    isSubmitting,
  };
}

